import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useInView,
  useReducedMotion,
} from "framer-motion";
import {
  FaBookOpen,
  FaProjectDiagram,
  FaGlobe,
  FaLightbulb,
  FaHandsHelping,
  FaFlask,
  FaBrain,
  FaComments,
  FaShieldAlt,
  FaUsers,
  FaArrowRight,
  FaCheckCircle,
  FaGraduationCap,
  FaLaptopCode,
  FaStar,
  FaLanguage,
  FaCalculator,
  FaGlobeAsia,
  FaMosque,
  FaRunning,
  FaSearch,
  FaPencilAlt,
  FaTools,
  FaMicrophone,
  FaFutbol,
  FaPalette,
  FaBus,
  FaPlus,
  FaMinus,
  FaPaperPlane,
} from "react-icons/fa";

import "./MiddleSchool.css";

/* Change this if your admission route is different (routes are case-sensitive) */
const APPLY_PATH = "/ApplyForm";

/* ------------------------------------------------------------------ */
/* Data (outside component => not re-created on each render)           */
/* ------------------------------------------------------------------ */
const highlights = [
  { icon: <FaFlask />, title: "STEM Focus", text: "Hands-on science, technology and maths" },
  { icon: <FaBrain />, title: "Critical Thinking", text: "Questions, reasoning and problem-solving" },
  { icon: <FaHandsHelping />, title: "Service & Character", text: "Responsible, kind and confident students" },
  { icon: <FaGraduationCap />, title: "Grades 3–8", text: "A complete middle school journey" },
];

const pillars = [
  {
    word: "STEM",
    icon: <FaFlask />,
    title: "STEM",
    text: "Science, Technology, Engineering and Mathematics explored through experiments, building and real problem-solving.",
    points: ["Practical experiments", "Logical problem-solving", "Digital awareness"],
  },
  {
    word: "SKILLS",
    icon: <FaBrain />,
    title: "Skills",
    text: "Communication, critical thinking, teamwork and study habits that last well beyond the classroom.",
    points: ["Clear communication", "Critical thinking", "Teamwork & leadership"],
  },
  {
    word: "SERVICE",
    icon: <FaHandsHelping />,
    title: "Service",
    text: "Character, kindness and responsibility shown by helping others and contributing to the community.",
    points: ["Integrity & kindness", "Personal responsibility", "Community contribution"],
  },
];

const learningPrograms = [
  { icon: <FaFlask />, number: "01", title: "STEM Education", color: "blue", description: "Explore Science, Technology, Engineering and Mathematics through practical activities, experiments and real-world problem-solving." },
  { icon: <FaProjectDiagram />, number: "02", title: "Project-Based Learning", color: "orange", description: "Connect classroom concepts with meaningful projects that encourage collaboration, creativity and independent thinking." },
  { icon: <FaBrain />, number: "03", title: "Critical Thinking", color: "purple", description: "Develop analytical skills, logical reasoning and the confidence to ask questions, evaluate information and explore solutions." },
  { icon: <FaGlobe />, number: "04", title: "Language Development", color: "green", description: "Strengthen reading, writing, vocabulary, listening and speaking skills to support effective communication." },
  { icon: <FaLaptopCode />, number: "05", title: "Technology & Innovation", color: "pink", description: "Encourage digital awareness, creative thinking and an understanding of how technology can solve everyday problems." },
  { icon: <FaLightbulb />, number: "06", title: "Lifelong Learning", color: "yellow", description: "Build curiosity, adaptability and positive learning habits that help students embrace new challenges with confidence." },
];

const subjects = [
  { icon: <FaBookOpen />, title: "English", text: "Reading comprehension, writing, vocabulary and speaking." },
  { icon: <FaLanguage />, title: "Urdu", text: "Fluent reading, writing and expression in the national language." },
  { icon: <FaCalculator />, title: "Mathematics", text: "Number work, algebra basics, geometry and reasoning." },
  { icon: <FaFlask />, title: "Science", text: "Understanding the world through observation and experiments." },
  { icon: <FaGlobeAsia />, title: "Social Studies", text: "History, geography, civics and responsible citizenship." },
  { icon: <FaMosque />, title: "Islamiyat", text: "Values, ethics and teachings explained with understanding." },
  { icon: <FaLaptopCode />, title: "Computer Science", text: "Digital skills, logical thinking and safe technology use." },
  { icon: <FaRunning />, title: "Arts & Physical Education", text: "Creativity, fitness and healthy habits for balanced growth." },
];

const learningCycle = [
  { icon: <FaSearch />, title: "Explore", text: "Students ask questions and investigate new ideas." },
  { icon: <FaPencilAlt />, title: "Practise", text: "Concepts are strengthened through guided practice." },
  { icon: <FaTools />, title: "Create", text: "Knowledge is applied in projects, models and presentations." },
  { icon: <FaComments />, title: "Reflect", text: "Feedback and discussion show what to improve next." },
];

const studentSkills = [
  { icon: <FaBookOpen />, title: "Academic Excellence", description: "Build strong foundations across core subjects while developing effective study habits and a deeper understanding of concepts." },
  { icon: <FaUsers />, title: "Collaboration & Teamwork", description: "Learn to share ideas, respect different perspectives and work productively with classmates." },
  { icon: <FaHandsHelping />, title: "Character & Service", description: "Encourage kindness, integrity, responsibility and a positive contribution to the school and wider community." },
  { icon: <FaShieldAlt />, title: "Confidence & Responsibility", description: "Develop independence, resilience, self-discipline and the confidence to take on new learning challenges." },
];

const grades = [
  { range: "03–04", icon: <FaBookOpen />, title: "Build Foundations", text: "Strengthen core concepts, reading comprehension, numeracy and classroom participation.", chips: ["Reading", "Numeracy", "Participation"] },
  { range: "05–06", icon: <FaFlask />, title: "Explore & Apply", text: "Develop subject knowledge, practical understanding, teamwork and independent thinking.", chips: ["Projects", "Teamwork", "Independence"] },
  { range: "07–08", icon: <FaLightbulb />, title: "Think & Innovate", text: "Build analytical reasoning, self-management and readiness for more advanced academic challenges.", chips: ["Analysis", "Self-management", "Readiness"] },
];

const activities = [
  { icon: <FaFlask />, title: "Science Exhibitions", text: "Students present models and experiments to teachers and parents." },
  { icon: <FaMicrophone />, title: "Debates & Public Speaking", text: "Building confident voices through speeches and discussions." },
  { icon: <FaFutbol />, title: "Sports & Fitness", text: "Teamwork, discipline and healthy competition on the field." },
  { icon: <FaPalette />, title: "Creative Arts", text: "Drawing, craft and design that let imagination lead." },
  { icon: <FaHandsHelping />, title: "Service Projects", text: "Practical ways to help others and the community." },
  { icon: <FaBus />, title: "Educational Trips", text: "Learning outside the classroom through safe, guided visits." },
];

const readyPoints = [
  "Strong subject foundations",
  "Study and exam-readiness habits",
  "Independent, self-managed learning",
  "Regular teacher guidance and feedback",
];

const admissionSteps = [
  { title: "Inquire", text: "Visit the school or contact the admission office to learn about the programme." },
  { title: "Apply", text: "Fill in the online application form with your child's and your details." },
  { title: "Meet & Assess", text: "A friendly interaction helps us place your child in the right grade." },
  { title: "Confirm", text: "Complete the required documents and admission formalities." },
  { title: "Start Learning", text: "Your child joins the class and begins an exciting new journey." },
];

const faqs = [
  { q: "What does “STEM, Skills, Service” mean?", a: "It is the guiding motto of our education: STEM builds knowledge, Skills build abilities such as communication and critical thinking, and Service builds character and responsibility." },
  { q: "Which grades are part of the middle school?", a: "Our middle school covers Grades 3 to 8. Please contact the admission office for age requirements and availability in the current session." },
  { q: "How can I apply for admission?", a: "Click “Apply Now”, fill in the online form and submit it. Our admission team will contact you with the next steps." },
  { q: "How do you track my child's progress?", a: "Through regular classroom assessments, teacher feedback and parent–teacher meetings, so you always know how your child is doing." },
  { q: "How is technology used in learning?", a: "Technology supports learning through digital skills, research and creative projects, with a focus on safe and responsible use." },
];

/* ------------------------------------------------------------------ */
/* Animation helpers                                                   */
/* ------------------------------------------------------------------ */
const EASE = [0.22, 1, 0.36, 1];
const VIEW = { once: true, amount: 0.15 };

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};
const pop = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
};
const stagger = (gap = 0.1) => ({
  hidden: {},
  show: { transition: { staggerChildren: gap } },
});

const SectionHeading = ({ label, title, highlight, text }) => (
  <motion.header
    className="ags-middle-heading"
    variants={fade}
    initial="hidden"
    whileInView="show"
    viewport={VIEW}
  >
    <span className="ags-middle-label">{label}</span>
    <h2>
      {title}
      {highlight && <span> {highlight}</span>}
    </h2>
    {text && <p>{text}</p>}
  </motion.header>
);

/* Each grid child sits inside <Cell> so framer's inline transform
   never fights with the CSS :hover transform on the card. */
const Grid = ({ className = "", children, gap = 0.09 }) => (
  <motion.div
    className={className}
    variants={stagger(gap)}
    initial="hidden"
    whileInView="show"
    viewport={VIEW}
  >
    {children}
  </motion.div>
);
const Cell = ({ children, variants = fade }) => (
  <motion.div className="ags-middle-cell" variants={variants}>
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const MiddleSchool = () => {
  const reduce = useReducedMotion();
  const [openFaq, setOpenFaq] = useState(null);

  const heroRef = useRef(null);
  const artRef = useRef(null);
  const closingRef = useRef(null);
  const heroInView = useInView(heroRef, { amount: 0.1 });
  const closingInView = useInView(closingRef, { amount: 0.2 });
  const showFab = !heroInView && !closingInView;

  const goTo = (e, id) => {
    e.preventDefault();
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  /* Mouse-only 3D tilt on the hero card (no re-renders, touch/reduced-motion safe) */
  const onTilt = (e) => {
    if (reduce || e.pointerType !== "mouse" || !artRef.current) return;
    const r = artRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    artRef.current.style.setProperty("--ry", `${(-5 + x * 14).toFixed(2)}deg`);
    artRef.current.style.setProperty("--rx", `${(2 - y * 12).toFixed(2)}deg`);
  };
  const resetTilt = () => {
    artRef.current?.style.removeProperty("--ry");
    artRef.current?.style.removeProperty("--rx");
  };

  return (
    <MotionConfig reducedMotion="user">
      <main className="ags-middle">
        {/* ================= Hero ================= */}
        <section className="ags-middle-hero" ref={heroRef}>
          <div className="ags-middle-container ags-middle-hero-grid">
            <motion.div
              className="ags-middle-hero-copy"
              variants={stagger(0.13)}
              initial="hidden"
              animate="show"
            >
              <motion.span className="ags-middle-eyebrow" variants={fade}>
                ACADEMIC GROUP OF SCHOOLS & COLLEGE
              </motion.span>

              <motion.span className="ags-middle-level" variants={fade}>
                <FaGraduationCap /> MIDDLE SCHOOL · GRADES 3–8
              </motion.span>

              <motion.h1 variants={fade}>
                Growing Knowledge.
                <span> Shaping Future Leaders.</span>
              </motion.h1>

              <motion.p variants={fade}>
                At Academic Group of Schools (AGS), the middle school years are
                an opportunity to explore new ideas, strengthen academic
                foundations and discover individual potential. We encourage
                students to think critically, embrace innovation and develop the
                skills needed for an ever-changing world.
              </motion.p>

              <motion.div className="ags-middle-hero-actions" variants={fade}>
                <Link to={APPLY_PATH} className="ags-middle-btn">
                  Apply Now <FaArrowRight />
                </Link>
                <a
                  href="#middle-programs"
                  onClick={(e) => goTo(e, "middle-programs")}
                  className="ags-middle-btn ags-middle-btn-outline"
                >
                  Explore Our Learning
                </a>
              </motion.div>

              <motion.div className="ags-middle-hero-promise" variants={fade}>
                <FaCheckCircle />
                <span>STEM · SKILLS · SERVICE</span>
              </motion.div>
            </motion.div>

            <motion.div
              className="ags-middle-hero-art"
              ref={artRef}
              onPointerMove={onTilt}
              onPointerLeave={resetTilt}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.25 }}
            >
              <div className="ags-middle-orbit ags-middle-orbit-one" />
              <div className="ags-middle-orbit ags-middle-orbit-two" />

              <div className="ags-middle-art-main">
                <div className="ags-middle-art-cap">
                  <FaGraduationCap />
                </div>
                <span>THE NEXT GENERATION</span>
                <h2>Think Big.</h2>
                <h2>Explore More.</h2>
                <p>Knowledge today. Possibilities tomorrow.</p>
                <div className="ags-middle-art-icons">
                  <span><FaFlask /></span>
                  <span><FaBookOpen /></span>
                  <span><FaLaptopCode /></span>
                </div>
              </div>

              <div className="ags-middle-float ags-middle-float-top">
                <FaLightbulb />
                <span>Think Creatively</span>
              </div>
              <div className="ags-middle-float ags-middle-float-bottom">
                <FaStar />
                <span>Achieve More</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ================= Highlights strip ================= */}
        <Grid className="ags-middle-container ags-middle-highlights" gap={0.1}>
          {highlights.map((h) => (
            <motion.div className="ags-middle-highlight" key={h.title} variants={pop}>
              <span className="ags-middle-highlight-icon">{h.icon}</span>
              <div>
                <strong>{h.title}</strong>
                <span>{h.text}</span>
              </div>
            </motion.div>
          ))}
        </Grid>

        {/* ================= Introduction ================= */}
        <section className="ags-middle-section ags-middle-intro">
          <div className="ags-middle-container ags-middle-intro-grid">
            <motion.div
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <span className="ags-middle-label">OUR EDUCATIONAL VISION</span>
              <h2>
                A Strong Foundation for
                <span> a Changing World</span>
              </h2>
              <motion.div
                className="ags-middle-accent-line"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
              />
            </motion.div>

            <Grid className="ags-middle-intro-copy" gap={0.15}>
              <motion.p variants={fade}>
                Academic Group of Schools (AGS) aims to prepare students for the
                demands of tomorrow by encouraging academic achievement,
                personal growth and social responsibility. Our educational
                philosophy is guided by the motto
                <strong> STEM, SKILLS, SERVICE.</strong>
              </motion.p>
              <motion.p variants={fade}>
                Across Grades 3 to 8, students can build their understanding
                through structured lessons, practical activities, collaborative
                projects and opportunities to explore different disciplines.
              </motion.p>
              <motion.p variants={fade}>
                We believe education extends beyond textbooks. Developing
                curiosity, good character, communication and problem-solving
                skills helps students prepare for future academic challenges.
              </motion.p>
            </Grid>
          </div>
        </section>

        {/* ================= Motto pillars ================= */}
        <section className="ags-middle-section ags-middle-dark">
          <div className="ags-middle-container">
            <SectionHeading
              label="OUR MOTTO"
              title="STEM, Skills,"
              highlight="Service"
              text="Three ideas that guide every lesson, activity and school day in our middle school."
            />

            <Grid className="ags-middle-pillar-grid" gap={0.14}>
              {pillars.map((p) => (
                <Cell key={p.word}>
                  <article className="ags-middle-pillar" data-word={p.word}>
                    <span className="ags-middle-pillar-icon">{p.icon}</span>
                    <h3>{p.title}</h3>
                    <p>{p.text}</p>
                    <ul>
                      {p.points.map((pt) => (
                        <li key={pt}><FaCheckCircle /> {pt}</li>
                      ))}
                    </ul>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Learning programs ================= */}
        <section
          className="ags-middle-section ags-middle-alt ags-middle-programs"
          id="middle-programs"
        >
          <div className="ags-middle-container">
            <SectionHeading
              label="LEARN THROUGH DISCOVERY"
              title="Our Learning Programs"
              highlight="& Educational Approach"
              text="Encouraging students to question, create, communicate and apply their knowledge through a broad learning experience."
            />

            <Grid className="ags-middle-program-grid">
              {learningPrograms.map((program) => (
                <Cell key={program.number}>
                  <article className={`ags-middle-program-card ags-middle-${program.color}`}>
                    <div className="ags-middle-program-top">
                      <div className="ags-middle-program-icon">{program.icon}</div>
                      <span>{program.number}</span>
                    </div>
                    <h3>{program.title}</h3>
                    <p>{program.description}</p>
                    <div className="ags-middle-program-footer">
                      <span>Discover your potential</span>
                      <FaArrowRight />
                    </div>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Subjects ================= */}
        <section className="ags-middle-section">
          <div className="ags-middle-container">
            <SectionHeading
              label="WHAT STUDENTS STUDY"
              title="A Broad Curriculum for"
              highlight="Curious Minds"
              text="Core subjects build strong academic skills, while creative and practical subjects help students discover their strengths."
            />

            <Grid className="ags-middle-subject-grid">
              {subjects.map((s) => (
                <Cell key={s.title} variants={pop}>
                  <article className="ags-middle-subject-card">
                    <div className="ags-middle-subject-icon">{s.icon}</div>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Learning cycle (a real sequence) ================= */}
        <section className="ags-middle-section ags-middle-alt">
          <div className="ags-middle-container">
            <SectionHeading
              label="HOW LEARNING HAPPENS"
              title="From Curiosity to"
              highlight="Confident Understanding"
              text="Our lessons follow a simple cycle that turns questions into lasting knowledge."
            />

            <Grid className="ags-middle-cycle-grid" gap={0.14}>
              {learningCycle.map((s, i) => (
                <Cell key={s.title} variants={pop}>
                  <article className="ags-middle-step">
                    <span className="ags-middle-step-marker">{s.icon}</span>
                    <small>Step {i + 1}</small>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Skills ================= */}
        <section className="ags-middle-section ags-middle-skills">
          <div className="ags-middle-container ags-middle-skills-grid">
            <motion.div
              className="ags-middle-skills-copy"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={VIEW}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <span className="ags-middle-label">BEYOND THE CLASSROOM</span>
              <h2>
                Skills for School.
                <span> Skills for Life.</span>
              </h2>
              <p>
                Middle school is an important stage for developing independence,
                responsibility and confidence. At AGS, our educational vision
                values both academic progress and the personal skills that help
                students participate positively in the world around them.
              </p>
              <div className="ags-middle-skills-highlight">
                <FaGraduationCap />
                <div>
                  <strong>Preparing for Tomorrow</strong>
                  <p>
                    Encouraging students to become thoughtful learners,
                    effective communicators and responsible citizens.
                  </p>
                </div>
              </div>
            </motion.div>

            <Grid className="ags-middle-skills-list" gap={0.12}>
              {studentSkills.map((skill, index) => (
                <motion.article className="ags-middle-skill-item" key={skill.title} variants={fade}>
                  <div className="ags-middle-skill-icon">{skill.icon}</div>
                  <div>
                    <span>SKILL {String(index + 1).padStart(2, "0")}</span>
                    <h3>{skill.title}</h3>
                    <p>{skill.description}</p>
                  </div>
                </motion.article>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Grade journey ================= */}
        <section className="ags-middle-section ags-middle-alt">
          <div className="ags-middle-container">
            <SectionHeading
              label="THE STUDENT JOURNEY"
              title="Progressing Through"
              highlight="Grades 3 to 8"
              text="Each stage offers opportunities to strengthen knowledge, develop independence and prepare for more advanced learning."
            />

            <Grid className="ags-middle-grade-grid" gap={0.14}>
              {grades.map((g) => (
                <Cell key={g.range}>
                  <article className="ags-middle-grade-card">
                    <span className="ags-middle-grade-number">{g.range}</span>
                    <div className="ags-middle-grade-icon">{g.icon}</div>
                    <h3>{g.title}</h3>
                    <p>{g.text}</p>
                    <div className="ags-middle-chips">
                      {g.chips.map((c) => (
                        <span key={c}>{c}</span>
                      ))}
                    </div>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Activities ================= */}
        <section className="ags-middle-section">
          <div className="ags-middle-container">
            <SectionHeading
              label="ACTIVITIES & EVENTS"
              title="Learning That Goes"
              highlight="Beyond Books"
              text="Clubs, events and service projects help students build confidence, friendships and leadership."
            />

            <Grid className="ags-middle-activity-grid">
              {activities.map((a) => (
                <Cell key={a.title} variants={pop}>
                  <article className="ags-middle-activity-card">
                    <span className="ags-middle-activity-icon">{a.icon}</span>
                    <div>
                      <h3>{a.title}</h3>
                      <p>{a.text}</p>
                    </div>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ================= Leadership + readiness ================= */}
        <section className="ags-middle-section ags-middle-alt ags-middle-leadership" id="middle-approach">
          <div className="ags-middle-container">
            <motion.div
              className="ags-middle-leadership-panel"
              variants={stagger(0.12)}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <motion.div className="ags-middle-leadership-icon" variants={pop}>
                <FaComments />
              </motion.div>

              <motion.div className="ags-middle-leadership-copy" variants={fade}>
                <span className="ags-middle-label">OUR COMMITMENT</span>
                <h2>
                  Developing Confident,
                  <span> Responsible Future Leaders</span>
                </h2>
                <p>
                  We aim to encourage students to communicate clearly, respect
                  different perspectives, work together and approach challenges
                  with determination. By combining academic learning with
                  positive values and a spirit of service, AGS seeks to support
                  the development of capable and compassionate young people.
                </p>
                <ul className="ags-middle-ready-list">
                  {readyPoints.map((p) => (
                    <li key={p}><FaCheckCircle /> {p}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="ags-middle-leadership-note" variants={fade}>
                <FaHandsHelping />
                <span>Learn with purpose. Lead with responsibility.</span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ================= Admission process ================= */}
        <section className="ags-middle-section" id="middle-admission">
          <div className="ags-middle-container">
            <SectionHeading
              label="ADMISSION PROCESS"
              title="Joining Our School Is"
              highlight="Simple and Easy"
              text="Five clear steps from your first inquiry to your child's first day."
            />

            <Grid className="ags-middle-process-grid" gap={0.12}>
              {admissionSteps.map((s, i) => (
                <Cell key={s.title} variants={pop}>
                  <article className="ags-middle-step ags-middle-step-plain">
                    <span className="ags-middle-step-number">{i + 1}</span>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>

            <motion.div
              className="ags-middle-process-cta"
              variants={fade}
              initial="hidden"
              whileInView="show"
              viewport={VIEW}
            >
              <Link to={APPLY_PATH} className="ags-middle-btn">
                Start Your Application <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ================= FAQ ================= */}
        <section className="ags-middle-section ags-middle-alt">
          <div className="ags-middle-container ags-middle-narrow">
            <SectionHeading
              label="PARENTS' QUESTIONS"
              title="Frequently Asked"
              highlight="Questions"
            />

            <Grid className="ags-middle-faq" gap={0.08}>
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <motion.div
                    key={f.q}
                    variants={fade}
                    className={`ags-middle-faq-item ${open ? "is-open" : ""}`}
                  >
                    <button
                      type="button"
                      id={`ms-faq-btn-${i}`}
                      aria-expanded={open}
                      aria-controls={`ms-faq-${i}`}
                      onClick={() => setOpenFaq(open ? null : i)}
                    >
                      <span>{f.q}</span>
                      {open ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
                    </button>
                    <div
                      id={`ms-faq-${i}`}
                      className="ags-middle-faq-body"
                      role="region"
                      aria-labelledby={`ms-faq-btn-${i}`}
                    >
                      <div><p>{f.a}</p></div>
                    </div>
                  </motion.div>
                );
              })}
            </Grid>
          </div>
        </section>

        {/* ================= Closing CTA ================= */}
        <section className="ags-middle-closing" ref={closingRef}>
          <motion.div
            className="ags-middle-container ags-middle-closing-content"
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={VIEW}
          >
            <motion.div className="ags-middle-closing-icon" variants={pop}>
              <FaGraduationCap />
            </motion.div>

            <motion.span variants={fade}>ACADEMIC GROUP OF SCHOOLS & COLLEGE</motion.span>

            <motion.h2 variants={fade}>
              Inspiring Potential.
              <br />
              Empowering the Future.
            </motion.h2>

            <motion.p variants={fade}>
              Every new idea is an opportunity to grow. Through learning,
              innovation and service, we aim to help students build the
              knowledge, character and confidence to shape their future.
            </motion.p>

            <motion.div variants={fade}>
              <Link to={APPLY_PATH} className="ags-middle-btn">
                Apply Now <FaArrowRight />
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* ================= Floating apply button (phones & tablets) ================= */}
        <AnimatePresence>
          {showFab && (
            <motion.div
              className="ags-middle-fab"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.4, duration: 0.4 } }}
              exit={{ opacity: 0, y: 24, transition: { duration: 0.2 } }}
            >
              <Link to={APPLY_PATH} aria-label="Apply for admission">
                <FaPaperPlane aria-hidden="true" /> Apply Now
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </MotionConfig>
  );
};

export default MiddleSchool;
